import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SecureMediaImg from "components/SecureMediaImg";

const APP_BASE_URL = process.env.REACT_APP_APP_BASE_URL || "";

const CustomerQrDrawer = ({
    isVisible,
    isOpen,
    styles,
    title,
    data,
    closeLabel,
    onClose,
}) => {
    const { t } = useTranslation();
    const [shareFeedback, setShareFeedback] = useState("");
    const feedbackTimeoutRef = useRef(null);
    const resolvedTargetUrl = useMemo(() => {
        const originalUrl = data?.target_url;
        if (!originalUrl) {
            return "";
        }

        const buildUrl = (path) => {
            if (!APP_BASE_URL) {
                return path;
            }
            const normalizedBase = APP_BASE_URL.endsWith("/")
                ? APP_BASE_URL.slice(0, -1)
                : APP_BASE_URL;
            const normalizedPath = path.startsWith("/") ? path : `/${path}`;
            return `${normalizedBase}${normalizedPath}`;
        };

        try {
            const parsed = new URL(originalUrl);
            const combinedPath = `${parsed.pathname}${parsed.search}${parsed.hash}`;
            if (APP_BASE_URL) {
                return buildUrl(combinedPath);
            }
            return originalUrl;
        } catch (error) {
            return APP_BASE_URL ? buildUrl(originalUrl) : originalUrl;
        }
    }, [data?.target_url]);

    useEffect(() => {
        return () => {
            if (feedbackTimeoutRef.current) {
                clearTimeout(feedbackTimeoutRef.current);
            }
        };
    }, []);

    const updateFeedback = (message) => {
        if (feedbackTimeoutRef.current) {
            clearTimeout(feedbackTimeoutRef.current);
        }
        setShareFeedback(message);
        if (message) {
            feedbackTimeoutRef.current = setTimeout(() => setShareFeedback(""), 3000);
        }
    };

    if (!isVisible) {
        return null;
    }

    const handleShareClick = async () => {
        const shareUrl = resolvedTargetUrl;
        if (!shareUrl) {
            updateFeedback(t("customers.shareQrNoLink"));
            return;
        }

        try {
            if (navigator.share) {
                await navigator.share({
                    title: title || "Customer QR",
                    url: shareUrl,
                });
                updateFeedback(t("customers.shareQrNativeSuccess"));
                return;
            }
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(shareUrl);
                updateFeedback(t("customers.shareQrCopied"));
                return;
            }
            updateFeedback(t("customers.shareQrNotSupported"));
        } catch (error) {
            if (error?.name !== "AbortError") {
                updateFeedback(t("customers.shareQrCancelled"));
            }
        }
    };

    const overlayStyle = {
        ...styles.overlay,
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? "auto" : "none",
    };

    const panelStyle = {
        ...styles.panel,
        transform: isOpen ? "translateX(0)" : "translateX(100%)",
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div
                style={panelStyle}
                onClick={(event) => {
                    event.stopPropagation();
                }}
            >
                <div style={styles.header}>
                    <h6 style={styles.title}>{title}</h6>
                    <button style={styles.closeBtn} onClick={onClose}>
                        {closeLabel}
                    </button>
                </div>
                <div style={styles.body}>
                    <div
                        className="d-flex justify-content-center align-items-center flex-column"
                        style={{ flex: 1 }}
                    >
                        <SecureMediaImg
                            src={data?.qr_code?.url || ""}
                            alt="QR code"
                            className="card-img-top"
                            style={{ height: "300px", width: "300px", objectFit: "cover" }}
                            enabled={!!data?.qr_code?.url}
                        />
                        <a
                            href={resolvedTargetUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginTop: 10 }}
                        >
                            {resolvedTargetUrl}
                        </a>
                    </div>
                    <div
                        style={{
                            marginTop: "auto",
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                        }}
                    >
                        <button
                            type="button"
                            style={{
                                ...styles.shareButton,
                                opacity: resolvedTargetUrl ? 1 : 0.6,
                                cursor: resolvedTargetUrl ? "pointer" : "not-allowed",
                            }}
                            onClick={handleShareClick}
                            disabled={!resolvedTargetUrl}
                        >
                            {t("customers.shareQrButton")}
                        </button>
                        {shareFeedback && (
                            <div style={styles.shareFeedback}>{shareFeedback}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerQrDrawer;
